import { useEffect, useState, type ChangeEvent, type FC } from "react";

import "./stylesEditNoteModal.css";

import { Input } from "antd";
import { CloseOutlined, LoadingOutlined } from "@ant-design/icons";

import { AppStrings } from "../../../../../utils/constants/AppStrings";
import type { Note } from "../Notes/Notes";
import { NOTE_BODY_MAX_LENGTH } from "../../../../../queries/user-notes/types";

export interface EditNoteModalProps {
  handleCancel: () => void;
  handleNotesChange: (noteId: Note["note_id"], body: string) => void;
  editNote: Note;
  isSubmitting?: boolean;
}

const EditNoteModal: FC<EditNoteModalProps> = ({
  handleCancel,
  handleNotesChange,
  editNote,
  isSubmitting = false,
}) => {
  const [body, setBody] = useState<string>(editNote.body ?? "");

  useEffect(() => {
    setBody(editNote.body ?? "");
  }, [editNote]);

  const handleBodyChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setBody(e.target.value);
  };

  // While a save is in flight we lock the modal closed: the parent owns
  // the close lifecycle and will dismiss us on success, or keep us open
  // on error so the user can retry without losing the body. The form
  // state is reset by the parent's `destroyOnHidden`, which unmounts us
  // on every dismissal.
  const handleClose = () => {
    if (isSubmitting) return;
    handleCancel();
  };

  const trimmedBody = body.trim();
  const hasChanges = trimmedBody !== (editNote.body ?? "").trim();
  const canSubmit = trimmedBody.length > 0 && hasChanges && !isSubmitting;

  const handleSave = () => {
    if (!canSubmit) return;
    handleNotesChange(editNote.note_id, trimmedBody);
  };

  return (
    <div className="suspend-modal-container">
      <div className="modal-header">
        <div className="modal-header-title">
          <h2 className="modal-title">{AppStrings.EDIT_NOTE}</h2>
        </div>
        <CloseOutlined
          onClick={handleClose}
          className="close-icon"
          aria-disabled={isSubmitting}
          style={{
            cursor: isSubmitting ? "not-allowed" : "pointer",
            opacity: isSubmitting ? 0.5 : 1,
          }}
        />
      </div>

      <div className="divider" />

      <div className="modal-content">
        <div className="transaction-info-item">
          <Input.TextArea
            value={body}
            maxLength={NOTE_BODY_MAX_LENGTH}
            rows={3}
            placeholder={AppStrings.LEAVE_A_COMMENT}
            onChange={handleBodyChange}
            disabled={isSubmitting}
          />
          <span className="max-length">
            {body.length}/{NOTE_BODY_MAX_LENGTH}
          </span>
        </div>
      </div>

      <div className="divider" />
      <div className="modal-footer">
        <div className="buttons">
          <button
            className="cancel-button"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            {AppStrings.CLOSE}
          </button>
          <button
            className="save-button"
            onClick={handleSave}
            disabled={!canSubmit}
            aria-busy={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <LoadingOutlined aria-hidden /> {AppStrings.SAVING}
              </>
            ) : (
              AppStrings.SAVE
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditNoteModal;
